// components/Reports/AdditionsReportContent.js
import React, { useMemo } from "react";
import { useRfi } from "../../../contexts/RfiContext";

/**
 * AdditionsReportContent - Report component for Stage 7 (Additions)
 * Displays all additions data in a formatted table for PDF generation
 *
 * Data source: RfiContext.stagesData.additions.data
 * Columns: Picture, PIN, Part Description, Length, Width, Quantity, Part Type, Remarks, QC Verify
 */
const AdditionsReportContent = () => {
  // Get additions data from Context
  const { stagesData } = useRfi();
  const additionsData = stagesData.additions.data;

  // Format additions data for display
  const formattedAdditions = useMemo(() => {
    if (!additionsData || !Array.isArray(additionsData)) return [];

    return additionsData.map((item) => ({
      picture: item.picture || null,
      pin: item.pin || "—",
      partDescription: item.part_description || "—",
      length: item.length || "—",
      width: item.width || "—",
      quantity: item.quantity || "—",
      partType: item.part_type || "—",
      remarks: item.remarks || "—",
      qcVerify: item.qc_verify ? "✓" : "✗",
    }));
  }, [additionsData]);

  if (!additionsData || additionsData.length === 0) {
    return (
      <div
        style={{
          width: "100%",
          fontSize: "12px",
          color: "#222222",
          padding: "20px",
          textAlign: "center",
        }}
      >
        No additions data available
      </div>
    );
  }

  return (
    <div
      style={{ width: "100%", fontSize: "10px", color: "#222222" }}
      // data-pdf-section="additions-report"
    >
      {/* Header Section */}
      <div style={{ marginBottom: "16px" }}>
        <div
          style={{
            fontSize: "12px",
            fontWeight: "bold",
            marginBottom: "10px",
            padding: "6px 10px",
            backgroundColor: "#073642",
            color: "#fff",
            letterSpacing: "0.5px",
          }}
          data-pdf-section="additions-report-title"
        >
          ADDITIONS DETAILS
        </div>

        {/* Additions Table */}
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            border: "1px solid #cbd5e0",
            marginBottom: "12px",
            tableLayout: "fixed",
          }}
        >
          <thead>
            <tr
              style={{ backgroundColor: "#f8f9fa" }}
              data-pdf-section="additions-report-t-header-nomargin"
            >
              <th
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "5px 6px",
                  fontWeight: "bold",
                  textAlign: "left",
                  fontSize: "9px",
                  width: "15%",
                }}
              >
                Picture
              </th>
              <th
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "5px 6px",
                  fontWeight: "bold",
                  textAlign: "left",
                  fontSize: "9px",
                  width: "8%",
                }}
              >
                PIN
              </th>
              <th
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "5px 6px",
                  fontWeight: "bold",
                  textAlign: "left",
                  fontSize: "9px",
                  width: "18%",
                }}
              >
                Part Description
              </th>
              <th
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "5px 6px",
                  fontWeight: "bold",
                  textAlign: "left",
                  fontSize: "9px",
                  width: "8%",
                }}
              >
                Length
              </th>
              <th
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "5px 6px",
                  fontWeight: "bold",
                  textAlign: "left",
                  fontSize: "9px",
                  width: "8%",
                }}
              >
                Width
              </th>
              <th
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "5px 6px",
                  fontWeight: "bold",
                  textAlign: "left",
                  fontSize: "9px",
                  width: "8%",
                }}
              >
                Quantity
              </th>
              <th
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "5px 6px",
                  fontWeight: "bold",
                  textAlign: "left",
                  fontSize: "9px",
                  width: "12%",
                }}
              >
                Part Type
              </th>
              <th
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "5px 6px",
                  fontWeight: "bold",
                  textAlign: "left",
                  fontSize: "9px",
                  width: "15%",
                }}
              >
                Remarks
              </th>
              <th
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "5px 6px",
                  fontWeight: "bold",
                  textAlign: "center",
                  fontSize: "9px",
                  width: "8%",
                }}
              >
                QC Verify
              </th>
            </tr>
          </thead>
          <tbody>
            {formattedAdditions.map((addition, index) => (
              <tr
                key={index}
                data-pdf-section={`additions-report-row-${index}-nomargin`}
              >
                {/* Picture */}
                <td
                  style={{
                    border: "1px solid #cbd5e0",
                    padding: "5px 6px",
                    textAlign: "center",
                    verticalAlign: "middle",
                  }}
                >
                  {addition.picture ? (
                    <img
                      src={addition.picture}
                      alt={`Addition ${index + 1}`}
                      style={{
                        width: "80px",
                        height: "80px",
                        objectFit: "cover",
                        borderRadius: "4px",
                        border: "1px solid #e0e0e0",
                      }}
                      onError={(e) => {
                        e.target.style.display = "none";
                        e.target.parentElement.textContent = "—";
                      }}
                    />
                  ) : (
                    "—"
                  )}
                </td>

                {/* PIN */}
                <td
                  style={{
                    border: "1px solid #cbd5e0",
                    padding: "5px 6px",
                    textAlign: "left",
                    fontSize: "9px",
                    wordWrap: "break-word",
                  }}
                >
                  {addition.pin}
                </td>

                {/* Part Description */}
                <td
                  style={{
                    border: "1px solid #cbd5e0",
                    padding: "5px 6px",
                    textAlign: "left",
                    fontSize: "9px",
                    wordWrap: "break-word",
                  }}
                >
                  {addition.partDescription}
                </td>

                {/* Length */}
                <td
                  style={{
                    border: "1px solid #cbd5e0",
                    padding: "5px 6px",
                    textAlign: "left",
                    fontSize: "9px",
                  }}
                >
                  {addition.length}
                </td>

                {/* Width */}
                <td
                  style={{
                    border: "1px solid #cbd5e0",
                    padding: "5px 6px",
                    textAlign: "left",
                    fontSize: "9px",
                  }}
                >
                  {addition.width}
                </td>

                {/* Quantity */}
                <td
                  style={{
                    border: "1px solid #cbd5e0",
                    padding: "5px 6px",
                    textAlign: "left",
                    fontSize: "9px",
                  }}
                >
                  {addition.quantity}
                </td>

                {/* Part Type */}
                <td
                  style={{
                    border: "1px solid #cbd5e0",
                    padding: "5px 6px",
                    textAlign: "left",
                    fontSize: "9px",
                    wordWrap: "break-word",
                  }}
                >
                  {addition.partType}
                </td>

                {/* Remarks */}
                <td
                  style={{
                    border: "1px solid #cbd5e0",
                    padding: "5px 6px",
                    textAlign: "left",
                    fontSize: "9px",
                    wordWrap: "break-word",
                  }}
                >
                  {addition.remarks}
                </td>

                {/* QC Verify */}
                <td
                  style={{
                    border: "1px solid #cbd5e0",
                    padding: "5px 6px",
                    textAlign: "center",
                    fontWeight: "bold",
                    fontSize: "12px",
                    color: addition.qcVerify === "✓" ? "#4caf50" : "#f44336",
                  }}
                >
                  {addition.qcVerify}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdditionsReportContent;
